import { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, User, Mail, Phone, CreditCard, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

interface WebinarFormData {
  nome_completo: string;
  email: string;
  telefone: string;
  cpf: string;
}

interface Props {
  onSubmit: (data: WebinarFormData) => void;
  isLoading: boolean;
}

export default function WebinarForm({ onSubmit, isLoading }: Props) {
  const [formData, setFormData] = useState<WebinarFormData>({
    nome_completo: '',
    email: '',
    telefone: '',
    cpf: ''
  });

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const handleChange = (field: keyof WebinarFormData, value: string) => {
    let formattedValue = value;
    if (field === 'cpf') formattedValue = formatCPF(value);
    if (field === 'telefone') formattedValue = formatPhone(value);
    
    setFormData(prev => ({ ...prev, [field]: formattedValue }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const inputClass = "bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-pink-500/50 focus:ring-pink-500/20 h-12 rounded-xl";

  return (
    <motion.form 
      onSubmit={handleSubmit}
      className="space-y-5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
    >
      <div className="space-y-2">
        <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
          <User className="w-4 h-4 text-pink-400" />
          Nome completo *
        </Label>
        <Input
          type="text"
          value={formData.nome_completo}
          onChange={(e) => handleChange('nome_completo', e.target.value)}
          placeholder="Seu nome completo"
          className={inputClass}
          required
        />
      </div>

      <div className="space-y-2">
        <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
          <Mail className="w-4 h-4 text-pink-400" />
          E-mail para contato *
        </Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          placeholder="seu@email.com"
          className={inputClass}
          required
        />
      </div>

      <div className="space-y-2">
        <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
          <Phone className="w-4 h-4 text-pink-400" />
          Telefone/WhatsApp (com DDD) *
        </Label>
        <Input
          type="tel"
          value={formData.telefone}
          onChange={(e) => handleChange('telefone', e.target.value)}
          placeholder="(00) 00000-0000"
          className={inputClass}
          maxLength={15}
          required
        />
      </div>

      <div className="space-y-2">
        <Label className="text-white/80 text-sm font-medium flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-pink-400" />
          CPF
        </Label>
        <Input
          type="text"
          value={formData.cpf}
          onChange={(e) => handleChange('cpf', e.target.value)}
          placeholder="000.000.000-00"
          className={inputClass}
          maxLength={14}
        />
      </div>

      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full h-14 bg-gradient-to-r from-pink-500 via-rose-500 to-orange-500 hover:from-pink-600 hover:via-rose-600 hover:to-orange-600 text-white font-semibold text-lg rounded-xl shadow-lg shadow-pink-500/25 transition-all duration-300"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Processando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Garantir minha vaga
            </span>
          )}
        </Button>
      </motion.div>
    </motion.form>
  );
}
